import { Form, Formik, type FormikHelpers } from 'formik';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import { toTimeConversion } from 'gcs-common/helper/houseAdministrationHelper';
import { houseNumbersValidator, ldaNumbersValidator, stringValidator } from 'gcs-common/helper/yupValidationHelper';
import {
  getIsFetchSelectedHouseSucceeded,
  getSelectedHouseDetails,
  getSelectedHouseLoading,
} from 'gcs-common/slices/administration/houses/housesSelectors';
import { houseSelectionReset } from 'gcs-common/slices/administration/houses/housesSlice';
import { fetchSelectedHouse } from 'gcs-common/slices/administration/houses/housesThunks';
import { fetchTeams } from 'gcs-common/slices/administration/teams/teamsThunks';
import { getHasPermission } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import backIcon from '../../../../img/back.svg';
import CheckIcon from '../../../../img/check_green.svg?react';
import CancelIcon from '../../../../img/close_red.svg?react';
import EditIcon from '../../../../img/edit.svg?react';
import FormikInput from '../../../FormikInput/FormikInput';
import FormikList from '../../../FormikList/FormikList';
import FormikOpeningHours from '../../../FormikOpeningHours/FormikOpeningHours';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import Tabs from '../../../Tabs/Tabs';
import styles from './styles.module.scss';

import { useUpdateHouseMutation } from 'gcs-common/clients/api/entities/houses/houses-api';
import { HOLIDAY_CODES } from '../../../../constants/holidayCodes';
import HouseCommunicationStructure from '../../HouseCommunicationStructure/CommunicationStructure/HouseCommunicationStructure/HouseCommunicationStructure';
import HouseCustomMessages from '../HouseCustomMessage/HouseCustomMessages';
import HouseDataSource from '../HouseDataSource/HouseDataSource';
import HouseReadConfirmation from '../HouseReadConfirmation/HouseReadConfirmation';

const requiredFields = [
  'MoDoStart',
  'MoDoEnd',
  'MoDoStartLateShift',
  'MoDoEndLateShift',
  'FrStart',
  'FrEnd',
  'FrStartLateShift',
  'FrEndLateShift',
];

const HouseCreateValidationSchema = Yup.object().shape({
  friendlyName: stringValidator,
  province: stringValidator,
  MoDoStart: Yup.string().required('Erforderlich'),
  MoDoEnd: Yup.string().required('Erforderlich'),
  FrStart: Yup.string().required('Erforderlich'),
  FrEnd: Yup.string().required('Erforderlich'),
  MoDoStartLateShift: Yup.string().required('Erforderlich'),
  MoDoEndLateShift: Yup.string().required('Erforderlich'),
  FrStartLateShift: Yup.string().required('Erforderlich'),
  FrEndLateShift: Yup.string().required('Erforderlich'),
  houseNumbers: houseNumbersValidator,
  ldaNumbers: ldaNumbersValidator,
});

const HouseDetail = () => {
  const hasGlobalAdminPermission = useSelector(getHasPermission('global_admin'));

  const { id: houseId } = useParams<{ id: string }>();
  // const [preferenceChanged, setPreferenceChanged] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const selectedHouse = useSelector(getSelectedHouseDetails);
  const selectedHouseLoading = useSelector(getSelectedHouseLoading);
  const isSelectedHouseLoaded = useSelector(getIsFetchSelectedHouseSucceeded);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const updateHouseMutation = useUpdateHouseMutation();

  useEffect(() => {
    dispatch(fetchTeams());
  }, [dispatch]);

  useEffect(() => {
    // @ts-expect-error Redux thunk dispatch typing
    dispatch(fetchSelectedHouse({ houseId }));
    return () => {
      dispatch(houseSelectionReset());
    };
  }, [dispatch, houseId]);

  const cancelEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // setPreferenceChanged(false);
    setIsEditing(false);
    // setIsCanceled(true);
  }, []);

  const startEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditing(true);
  }, []);

  const holidaySelectOptions = HOLIDAY_CODES.map((h) => {
    const { name, code } = h;
    return { value: code, label: name };
  });

  const { businessUnitName, friendlyName, province, openingHours, lateShift, houseNumbers, ldaNumbers } =
    selectedHouse || {};

  const initialValues = useMemo(() => {
    // Note: Formik/React creates a warning if a value is "null"
    // so we set all fields to at least ""
    // https://formik.org/docs/api/formik#initialvalues-values
    const currentOpeningHours = toTimeConversion(openingHours);
    const currentLateShift = toTimeConversion(lateShift);
    return {
      businessUnitName: businessUnitName || '',
      friendlyName: friendlyName || '',
      province: province || '',
      MoDoStart: currentOpeningHours.MoDoStart || '',
      MoDoEnd: currentOpeningHours.MoDoEnd || '',
      FrStart: currentOpeningHours.FrStart || '',
      FrEnd: currentOpeningHours.FrEnd || '',
      MoDoStartLateShift: currentLateShift.MoDoStart || '',
      MoDoEndLateShift: currentLateShift.MoDoEnd || '',
      FrStartLateShift: currentLateShift.FrStart || '',
      FrEndLateShift: currentLateShift.FrEnd || '',
      houseNumbers: houseNumbers || '',
      ldaNumbers: ldaNumbers || '',
    };
  }, [openingHours, lateShift, businessUnitName, friendlyName, province, houseNumbers, ldaNumbers]);

  const submit = useCallback(
    async (values: typeof initialValues, { setSubmitting }: FormikHelpers<typeof initialValues>) => {
      const changedValues: Record<string, unknown> = Object.keys(values).reduce((red, key) => {
        // all of those should be passed to create a valid data range
        if ((values as Record<string, unknown>)[key] === (initialValues as Record<string, unknown>)[key]) return red;
        return Object.assign(red, { [key]: (values as Record<string, unknown>)[key] });
      }, {});

      if (changedValues && Object.keys(changedValues).length === 0) {
        setIsEditing(false);
        setSubmitting(false);
        return;
      }
      for (const key of requiredFields) {
        changedValues[key] = (values as Record<string, unknown>)[key];
      }

      try {
        await updateHouseMutation.mutateAsync(
          {
            path: {
              house_id: houseId as string,
            },
            body: changedValues,
          },
          {
            onSuccess: () => {
              setIsEditing(false);
            },
            onSettled: () => {
              setSubmitting(false);
            },
          },
        );
      } catch {
        // Error toast is handled by the mutation onError callback.
      }
    },
    [houseId, initialValues, updateHouseMutation],
  );

  return (
    <div data-testid="houseDetailComponent">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={HouseCreateValidationSchema}
        onSubmit={submit}
        validateOnChange
      >
        {({ isSubmitting, resetForm }) => (
          <Form noValidate>
            <div className={styles.headerRow}>
              <div className={styles.row}>
                {hasGlobalAdminPermission && (
                  <button className={styles.backButton} type="button" onClick={() => navigate(-1)}>
                    <img src={backIcon} alt="back-icon" />
                  </button>
                )}
                <div className={styles.inputHeaderText}>{friendlyName}</div>
              </div>
              {selectedHouseLoading && <LoadingIndicator padding="0px" />}
              <div className={styles.buttonsWrapper}>
                {isEditing ? (
                  <>
                    <button
                      data-testid="saveButton"
                      className={styles.saveButton}
                      type="submit"
                      disabled={isSubmitting}
                    >
                      <CheckIcon />
                    </button>
                    <button
                      className={styles.saveButton}
                      type="button"
                      onClick={(event) => {
                        resetForm();
                        cancelEditing(event);
                      }}
                      disabled={isSubmitting}
                    >
                      <CancelIcon />
                    </button>
                  </>
                ) : (
                  <button
                    data-testid="editButton"
                    className={styles.saveButton}
                    type="button"
                    onClick={startEditing}
                    disabled={!isSelectedHouseLoaded}
                  >
                    <EditIcon />
                  </button>
                )}
                {isSubmitting && <LoadingIndicator padding="0px" />}
              </div>
            </div>

            <div className={styles.row}>
              <FormikInput disabled={!isEditing} label="Name" name="friendlyName" />

              <FormikInput disabled label="Marke" name="businessUnitName" />
            </div>

            <div className={styles.row}>
              <FormikList
                disabled={!isEditing || !hasGlobalAdminPermission}
                label="LDA"
                name="ldaNumbers"
                initialValue={ldaNumbers}
                distinctValues
              />
              <FormikList
                disabled={!isEditing || !hasGlobalAdminPermission}
                label="Hausnummer"
                name="houseNumbers"
                initialValue={houseNumbers}
                distinctValues
              />
            </div>

            <div className={styles.row}>
              <FormikSelect
                disabled={!isEditing}
                label="Bundesland"
                name="province"
                placeholder="Bitte wählen"
                options={holidaySelectOptions}
                labelKey="label"
                valueKey="value"
              />
            </div>

            <div className={styles.row}>
              <FormikOpeningHours
                disabled={!isEditing}
                label="Öffnungszeiten"
                label1="Mo-DO"
                label2="Fr"
                start1="MoDoStart"
                end1="MoDoEnd"
                start2="FrStart"
                end2="FrEnd"
              />

              <FormikOpeningHours
                disabled={!isEditing}
                label="Spätschicht"
                label1="Mo-DO"
                label2="Fr"
                start1="MoDoStartLateShift"
                end1="MoDoEndLateShift"
                start2="FrStartLateShift"
                end2="FrEndLateShift"
              />
            </div>
          </Form>
        )}
      </Formik>

      <div className={styles.tabWrapper}>
        <Tabs
          onActiveTabChanged={() => {}}
          disabled={!isSelectedHouseLoaded}
          tabs={{
            communicationStructure: {
              name: 'Kommunikationsstruktur',
              component: <>{isSelectedHouseLoaded && <HouseCommunicationStructure houseId={houseId as string} />}</>,
            },
            customMessage: {
              name: 'Automatische Nachrichten',
              component: <>{isSelectedHouseLoaded && <HouseCustomMessages />}</>,
            },
            dateSource: {
              name: 'Datenquellen',
              component: <HouseDataSource />,
            },
            readConfirmation: {
              name: 'Lesebestätigungen',
              component: <HouseReadConfirmation />,
            },
          }}
        />
      </div>
    </div>
  );
};
export default HouseDetail;
